import React from 'react';
import withStyles from '@material-ui/styles/withStyles';
import { Field, Formik } from 'formik';
import { Grid, TextField, MenuItem, Button } from '@material-ui/core';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  COMPARE_CORROSION_LOGS_FILTERS,
  COMPARE_CORROSION_LOGS_FILTERS_LABELS,
  MAX_NUMBER_OF_FILES_ALLOWED,
} from 'features/wells/corrosion/helpers/corrosionlogs.constants';
import {
  loadCorrosionLogs,
  loadCorrosionLogFilters,
  loadCompareFilesData,
} from 'features/wells/corrosion/helpers/corrosionlogs.actions';
import { EMPTY_LIST } from 'app/app.constants';
import CheckBox from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlank from '@material-ui/icons/CheckBoxOutlineBlank';
import {
  selectCorrosionLogsFilters,
  selectCorrosionLogsList,
} from 'features/wells/corrosion/helpers/corrosionlogs.selectors';
import { useEffect, useState } from 'react';
import useWellbore from 'features/wells/hooks/useWellbore';
import { useLocation, useHistory } from 'react-router-dom';

const CompareLogsFilters = ({
  classes,
  corrosionLogs,
  wellboreId,
  dispatchLoadCorrosionLogs,
  dispatchLoadCorrosionLogFilters,
  dispatchLoadCompareFilesData,
  filters,
}) => {
  const [initialFormValues, setInitialFormValues] = useState({
    [COMPARE_CORROSION_LOGS_FILTERS.LOGS]: EMPTY_LIST,
    [COMPARE_CORROSION_LOGS_FILTERS.JOINTS]: EMPTY_LIST,
    [COMPARE_CORROSION_LOGS_FILTERS.DIRECTION]: EMPTY_LIST,
    [COMPARE_CORROSION_LOGS_FILTERS.CLASS]: EMPTY_LIST,
  });

  const [selectAllFilter, setSelectAllFilter] = useState({
    [COMPARE_CORROSION_LOGS_FILTERS.JOINTS]: false,
    [COMPARE_CORROSION_LOGS_FILTERS.DIRECTION]: false,
    [COMPARE_CORROSION_LOGS_FILTERS.CLASS]: false,
  });

  const [errorMsg, setErrorMsg] = useState('');

  const { wellbore } = useWellbore(wellboreId);
  const history = useHistory();
  const location = useLocation();

  const checkFilesSelected = (logs) =>
    logs.filter((log) => log.checked ?? log).length ===
    MAX_NUMBER_OF_FILES_ALLOWED;

  const setFilterOptions = (filter, values) => {
    setInitialFormValues((prevInitialFormValues) => ({
      ...prevInitialFormValues,
      [filter]: values,
    }));
  };

  const handleSelectAll = (filterName) => {
    const checkAllSelected = selectAllFilter[filterName];
    const allOptions = initialFormValues[filterName];
    const updatedOptions = allOptions.map((option) => ({
      ...option,
      checked: !checkAllSelected,
    }));
    setInitialFormValues((prev) => ({
      ...prev,
      [filterName]: updatedOptions,
    }));
    setSelectAllFilter((prevValue) => ({
      ...prevValue,
      [filterName]: !checkAllSelected,
    }));
  };

  const selectOption = (filterName, toggledValue) => {
    setInitialFormValues((prev) => {
      const oldOptions = prev[filterName];
      const checkedCount = oldOptions.filter((o) => o.checked).length;
      const toggledOption = oldOptions.find((o) => o.value === toggledValue);
      const isCurrentlyChecked = toggledOption.checked;
      const isChecking = !isCurrentlyChecked;
      if (
        filterName === COMPARE_CORROSION_LOGS_FILTERS.LOGS &&
        isChecking &&
        checkedCount >= MAX_NUMBER_OF_FILES_ALLOWED
      )
        return prev;

      const updatedOptions = oldOptions.map((o) =>
        o.value === toggledValue ? { ...o, checked: !o.checked } : o,
      );

      if (selectAllFilter[filterName])
        setSelectAllFilter((prev) => ({
          ...prev,
          [filterName]: false,
        }));

      if (updatedOptions.every(({ checked }) => checked))
        setSelectAllFilter((prev) => ({
          ...prev,
          [filterName]: true,
        }));

      return {
        ...prev,
        [filterName]: updatedOptions,
      };
    });
  };

  const applyFilter = () => {
    const keyMap = {
      logs: 'ids',
      joints: 'joints',
      class: 'classes',
      direction: 'directions',
    };

    const selectedFilters = {};

    Object.entries(initialFormValues).forEach(([filterName, options]) => {
      selectedFilters[keyMap[filterName]] = options
        .filter((opt) => opt.checked)
        .map((opt) => opt.value);
    });

    const numLogs = selectedFilters.ids.length;
    if (numLogs !== MAX_NUMBER_OF_FILES_ALLOWED) {
      setErrorMsg(
        `Please select exactly ${MAX_NUMBER_OF_FILES_ALLOWED} log files.`,
      );
      return;
    }

    setErrorMsg('');

    dispatchLoadCompareFilesData(selectedFilters);

    const target = `${location.pathname}/tabulardata`;

    if (!location.pathname.includes('tabulardata')) {
      history.push(target);
    }
  };

  const resetFilter = (setFieldValue) => {
    setInitialFormValues((prev) => ({
      [COMPARE_CORROSION_LOGS_FILTERS.LOGS]: prev[
        COMPARE_CORROSION_LOGS_FILTERS.LOGS
      ].map((o) => ({ ...o, checked: false })),
      [COMPARE_CORROSION_LOGS_FILTERS.JOINTS]: EMPTY_LIST,
      [COMPARE_CORROSION_LOGS_FILTERS.CLASS]: EMPTY_LIST,
      [COMPARE_CORROSION_LOGS_FILTERS.DIRECTION]: EMPTY_LIST,
    }));

    setSelectAllFilter({
      [COMPARE_CORROSION_LOGS_FILTERS.JOINTS]: false,
      [COMPARE_CORROSION_LOGS_FILTERS.DIRECTION]: false,
      [COMPARE_CORROSION_LOGS_FILTERS.CLASS]: false,
    });

    setFieldValue(COMPARE_CORROSION_LOGS_FILTERS.JOINTS, []);
    setFieldValue(COMPARE_CORROSION_LOGS_FILTERS.CLASS, []);
    setFieldValue(COMPARE_CORROSION_LOGS_FILTERS.DIRECTION, []);
    setFieldValue(COMPARE_CORROSION_LOGS_FILTERS.LOGS, []);
  };

  useEffect(() => {
    let newCorrosionLogs = corrosionLogs
      .sort(
        (date1, date2) =>
          new Date(date1.logDate).getTime() - new Date(date2.logDate).getTime(),
      )
      .reverse()
      .map((log) => ({
        label: log.fileName,
        value: log.id,
        checked: false,
      }));

    setFilterOptions(COMPARE_CORROSION_LOGS_FILTERS.LOGS, newCorrosionLogs);
  }, [corrosionLogs]);

  useEffect(() => {
    if (Object.keys(filters).length > 0)
      setInitialFormValues((prevValues) => {
        const reShape = (item) => ({
          value: item,
          label: String(item).toUpperCase(),
          checked: false,
        });

        const mapFiltersAccordingly = (obj, fn) =>
          Object.fromEntries(
            Object.entries(obj).map(([key, arr]) => [key, arr.map(fn)]),
          );

        const newFilters = mapFiltersAccordingly(filters, reShape);

        return {
          ...prevValues,
          [COMPARE_CORROSION_LOGS_FILTERS.JOINTS]: newFilters?.joints.reverse(),
          [COMPARE_CORROSION_LOGS_FILTERS.CLASS]: newFilters?.classes.reverse(),
          [COMPARE_CORROSION_LOGS_FILTERS.DIRECTION]:
            newFilters?.directions.reverse(),
        };
      });
  }, [filters]);

  useEffect(
    () => {
      if (
        checkFilesSelected(
          initialFormValues[COMPARE_CORROSION_LOGS_FILTERS.LOGS],
        )
      ) {
        const selectedLogIds = initialFormValues[
          COMPARE_CORROSION_LOGS_FILTERS.LOGS
        ].reduce((ids, log) => {
          if (log.checked) ids.push(log.value);
          return ids;
        }, []);
        dispatchLoadCorrosionLogFilters({ ids: selectedLogIds });
      }
    },
    // eslint-disable-next-line
    [initialFormValues[COMPARE_CORROSION_LOGS_FILTERS.LOGS]],
    dispatchLoadCorrosionLogFilters,
  );

  useEffect(() => {
    if (wellbore.get('externalWellboreExternalId')) {
      dispatchLoadCorrosionLogs(wellbore.get('externalWellboreExternalId'));
    }
  }, [wellbore, dispatchLoadCorrosionLogs]);

  return (
    <Formik initialValues={initialFormValues}>
      {({ handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {Object.entries(COMPARE_CORROSION_LOGS_FILTERS).map(
              ([key, value]) => {
                const isLogs = value === COMPARE_CORROSION_LOGS_FILTERS.LOGS;
                return (
                  <Grid item xs={2} key={value}>
                    <Field name={value}>
                      {({ field }) => {
                        const selectedValues = Array.isArray(
                          initialFormValues[value],
                        )
                          ? initialFormValues[value]
                              .filter((option) => option.checked)
                              .map((option) => option.label)
                          : [];

                        return (
                          <TextField
                            {...field}
                            select
                            label={COMPARE_CORROSION_LOGS_FILTERS_LABELS[key]}
                            key={COMPARE_CORROSION_LOGS_FILTERS_LABELS[key]}
                            fullWidth
                            value={selectedValues}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => {
                                if (!selected || selected.length === 0) {
                                  return '';
                                }
                                if (
                                  initialFormValues[value].length ===
                                    selected.length &&
                                  !isLogs
                                ) {
                                  return 'ALL';
                                }
                                if (selected.length > 3) {
                                  return 'Multiple values selected';
                                }
                                return selected.join(', ');
                              },
                            }}
                          >
                            {initialFormValues[value].size === 0 ? (
                              <MenuItem value="-1">No option present</MenuItem>
                            ) : (
                              !isLogs &&
                              initialFormValues[value].length > 0 && (
                                <MenuItem
                                  value="ALL"
                                  onClick={() =>
                                    handleSelectAll(value, setFieldValue)
                                  }
                                >
                                  {selectAllFilter[value] ? (
                                    <CheckBox />
                                  ) : (
                                    <CheckBoxOutlineBlank />
                                  )}
                                  All
                                </MenuItem>
                              )
                            )}

                            {isLogs &&
                              initialFormValues[value]?.length === 0 && (
                                <MenuItem value="-1">
                                  Please Upload Logs
                                </MenuItem>
                              )}

                            {initialFormValues[value]?.map((option) => {
                              return (
                                <MenuItem
                                  value={option.label}
                                  onClick={() => {
                                    selectOption(value, option.value);
                                  }}
                                >
                                  {option.checked ? (
                                    <CheckBox />
                                  ) : (
                                    <CheckBoxOutlineBlank />
                                  )}
                                  {value ===
                                  COMPARE_CORROSION_LOGS_FILTERS.CLASS
                                    ? 'Class '
                                    : null}
                                  {option.label}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        );
                      }}
                    </Field>
                  </Grid>
                );
              },
            )}
            <Grid item xs={4} className={classes.buttonContainer}>
              <Button
                title="Cancel"
                color="secondary"
                variant="contained"
                size="small"
                className={classes.buttons}
                onClick={() => resetFilter(setFieldValue)}
              >
                Reset All
              </Button>
              <Button
                title="Apply"
                color="primary"
                variant="contained"
                type="submit"
                size="small"
                className={classes.buttons}
                onClick={() => applyFilter()}
              >
                Apply
              </Button>
            </Grid>
            {errorMsg && (
              <Grid item xs={12}>
                <div style={{ color: 'red', marginBottom: 8 }}>{errorMsg}</div>
              </Grid>
            )}
          </Grid>
        </form>
      )}
    </Formik>
  );
};

const styles = (theme) => ({
  filterContainer: {},
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  buttons: {
    marginLeft: theme.spacing(2),
    width: '20%',
  },
});

export default compose(
  connect(
    (state) => ({
      corrosionLogs: selectCorrosionLogsList(state),
      filters: selectCorrosionLogsFilters(state),
    }),
    {
      dispatchLoadCorrosionLogs: loadCorrosionLogs,
      dispatchLoadCorrosionLogFilters: loadCorrosionLogFilters,
      dispatchLoadCompareFilesData: loadCompareFilesData,
    },
  ),
  withStyles(styles),
)(CompareLogsFilters);
